import React from 'react';

// Default SDM logo configuration
// Dimensions match theme values: largeWidth: 16.667rem, largeHeight: 2.167rem
const DEFAULT_SDM_LOGO = {
  url: 'https://dis-prod.assetful.loblaw.ca/content/dam/loblaw-companies-limited/creative-assets/sdmi/2023/evergreen/masthead/SDM%20Logo.svg',
  altText: 'Shoppers Drug Mart',
  width: '16.667rem',
  height: 'auto',
};

export interface LogoData {
  largeLogo?: {
    url?: string;
    altText?: string;
    height?: number;
    width?: number;
  };
  callToAction?: {
    url?: string;
  };
}

export interface MastHeadMainBarSkeletonSSRProps {
  /** Logo data from SDM header configuration */
  logo?: LogoData;
  /** Whether to hide the logo and show a skeleton placeholder instead */
  hideLogo?: boolean;
}

/**
 * SSR-safe MainBar skeleton component.
 * Uses pure inline styles for SSR compatibility (no Chakra UI).
 * Renders the logo (actual image when data provided) and skeleton placeholders
 * for store selector and search bar.
 */
export const MastHeadMainBarSkeletonSSR = ({
  logo,
  hideLogo = false,
}: MastHeadMainBarSkeletonSSRProps) => {
  // Use provided logo data, fall back to default SDM logo
  const logoUrl = logo?.largeLogo?.url || DEFAULT_SDM_LOGO.url;
  const logoAlt = logo?.largeLogo?.altText || DEFAULT_SDM_LOGO.altText;
  const logoLink = logo?.callToAction?.url || '/';

  return (
    <header data-testid="masthead-main-bar-skeleton-ssr" style={{ width: '100%' }}>
      {/* MainBar container */}
      <div
        style={{
          display: 'flex',
          position: 'relative',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          borderTopRightRadius: '1rem',
          borderTopLeftRadius: '1rem',
          marginTop: '-2.672rem',
          paddingLeft: '2.672rem',
          paddingRight: '2.672rem',
          width: '100%',
        }}
      >
        {/* MainBar content */}
        <div
          style={{
            display: 'flex',
            position: 'relative',
            gap: '2.672rem',
            alignItems: 'center',
            width: '100%',
            paddingTop: '2.672rem',
            paddingBottom: '2.672rem',
            maxWidth: '1440px',
          }}
        >
          {/* Branding section */}
          <div
            style={{
              display: 'flex',
              gap: '2.672rem',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* Logo - shows actual SDM logo when data available */}
            {!hideLogo ? (
              <a href={logoLink} style={{ display: 'block' }} aria-label={logoAlt}>
                <img
                  src={logoUrl}
                  alt={logoAlt}
                  style={{
                    maxWidth: DEFAULT_SDM_LOGO.width,
                    height: DEFAULT_SDM_LOGO.height,
                  }}
                />
              </a>
            ) : (
              <span
                data-testid="skeleton-image"
                aria-hidden="true"
                style={{
                  width: '120px',
                  height: '40px',
                  backgroundColor: '#EDF2F7',
                  borderRadius: '2px',
                  display: 'block',
                }}
              />
            )}

            {/* Store dropdown skeleton - Desktop only */}
            <span
              aria-hidden="true"
              style={{
                height: '2.5rem',
                width: '150px',
                backgroundColor: '#EDF2F7',
                borderRadius: '2px',
                display: 'block',
              }}
            />
          </div>

          {/* Search Bar skeleton */}
          <search style={{ flex: 1 }} aria-label="Search products">
            <span
              aria-hidden="true"
              style={{
                height: '2.5rem',
                width: '100%',
                backgroundColor: '#F7FAFC',
                borderRadius: '6px',
                border: '1px solid #E2E8F0',
                display: 'block',
              }}
            />
          </search>

          {/* Note: Right side icons (cart, account) are NOT included in skeleton */}
        </div>
      </div>
    </header>
  );
};

MastHeadMainBarSkeletonSSR.displayName = 'MastHeadMainBarSkeletonSSR';
